import { AnimaliaDetailHeroDTO } from '../../../../application/mappers/AnimaliaDetailMapper';
import { buildAnimaliaTaxonomyCanonicalRoute } from '../../../../application/taxonomy/AnimaliaNavigableTaxonomy';
import { animaliaDetailDesignTokens } from '../shared/animalia-detail-design-tokens';

export class MvAnimaliaDetailHero extends HTMLElement {
  private hero: AnimaliaDetailHeroDTO | null = null;

  private backHref = '/';

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  setHero(hero: AnimaliaDetailHeroDTO): void {
    this.hero = hero;
    this.render();
  }

  setBackHref(backHref: string): void {
    this.backHref = backHref;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private render(): void {
    if (!this.shadowRoot || !this.hero) {
      return;
    }

    const mediaBadges = [
      this.renderLicenseBadge(),
      this.renderArtistBadge(),
      this.renderAttributionBadge(),
      this.hero.wikipediaUrl
        ? `<a class="media-badge wiki-badge" href="${this.escapeAttribute(this.hero.wikipediaUrl)}" target="_blank" rel="noreferrer">Wikipedia</a>`
        : '',
    ]
      .filter((badge) => badge !== '')
      .join('');

    const media = this.hero.imageSrc
      ? `
          <div class="hero-figure-wrap">
            <a
              class="hero-figure"
              href="${this.escapeAttribute(this.hero.imageSrc)}"
              target="_blank"
              rel="noreferrer"
              aria-label="Abrir imagen completa de ${this.escapeAttribute(this.hero.imageAlt)} en una pestana nueva"
            >
              <img src="${this.escapeAttribute(this.hero.imageSrc)}" alt="${this.escapeAttribute(this.hero.imageAlt)}">
            </a>
            <div class="media-badges">${mediaBadges}</div>
          </div>
        `
      : `
          <div class="hero-figure-wrap no-image">
            <div class="hero-figure hero-fallback" aria-hidden="true">
              <span>Animalia</span>
            </div>
            <div class="media-badges">
              <span class="media-badge">Hero sin imagen publica</span>
               ${this.hero.wikipediaUrl ? `<a class="media-badge wiki-badge" href="${this.escapeAttribute(this.hero.wikipediaUrl)}" target="_blank" rel="noreferrer">Wikipedia</a>` : ''}
            </div>
          </div>
        `;

    this.shadowRoot.innerHTML = `
      <style>
        ${animaliaDetailDesignTokens}

        .back-link {
          display: inline-flex;
          align-items: center;
          margin-bottom: 18px;
          padding: 10px 16px;
          border-radius: 999px;
          text-decoration: none;
          background: rgba(250, 247, 241, 0.92);
          border: 1px solid rgba(49, 70, 61, 0.12);
          color: #31463d;
        }

        .hero {
          border-radius: 30px;
          padding: 28px;
          background:
            linear-gradient(135deg, rgba(49, 70, 61, 0.98), rgba(95, 120, 128, 0.82)),
            linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0));
          color: #f7f3ed;
          box-shadow: 0 18px 38px rgba(31, 42, 35, 0.1);
        }

        .hero-detail {
          display: grid;
          grid-template-columns: minmax(320px, 0.92fr) minmax(0, 1.08fr);
          gap: 28px;
          align-items: center;
        }

        h1 {
          margin: 0;
          font-family: Georgia, 'Times New Roman', serif;
          line-height: 1.02;
          font-size: clamp(2.2rem, 4vw, 4rem);
        }

        .subtitle {
          margin: 12px 0 0;
          font-size: 1rem;
          color: rgba(247, 243, 237, 0.84);
        }

        .hero-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 18px;
        }

        .hero-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 9px 12px;
          border-radius: 999px;
          background: rgba(247, 243, 237, 0.12);
          border: 1px solid rgba(255, 255, 255, 0.14);
          color: #f7f3ed;
        }

        .hero-pill-link {
          text-decoration: none;
          color: inherit;
        }

        .hero-pill-link:hover .hero-pill,
        .hero-pill-link:focus-visible .hero-pill {
          background: rgba(247, 243, 237, 0.2);
          outline: none;
        }

        .hero-figure-wrap {
          position: relative;
          min-height: 430px;
        }

        .hero-figure {
          position: relative;
          min-height: 430px;
          border-radius: 24px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.12);
        }

        .hero-figure img,
        .hero-fallback {
          display: block;
          width: 100%;
          height: 100%;
          aspect-ratio: 4 / 5;
          object-fit: cover;
        }

        .hero-fallback {
          display: grid;
          place-items: center;
          background:
            radial-gradient(circle at top left, rgba(235, 229, 219, 0.34), transparent 30%),
            linear-gradient(135deg, rgba(95, 120, 128, 0.92), rgba(49, 70, 61, 0.98));
          color: rgba(247, 243, 237, 0.84);
          font-family: Georgia, 'Times New Roman', serif;
          font-size: clamp(2rem, 4vw, 3rem);
        }

        .hero-figure::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(32, 39, 32, 0.04), rgba(32, 39, 32, 0.4));
        }

        .media-badges {
          position: absolute;
          left: 18px;
          right: 18px;
          bottom: 18px;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          z-index: 1;
          margin-bottom: 1em;
        }

        .media-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 9px 12px;
          border-radius: 999px;
          background: rgba(18, 21, 18, 0.88);
          color: #f7f3ed;
          border: 1px solid rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(6px);
          text-decoration: none;
          box-shadow: 0 8px 18px rgba(18, 21, 18, 0.14);
        }

        .wiki-badge {
          background: rgba(41, 57, 61, 0.92);
        }

        @media (max-width: 980px) {
          .hero-detail {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 720px) {
          .hero {
            padding-left: 16px;
            padding-right: 16px;
          }

          .hero-figure-wrap,
          .hero-figure {
            min-height: 320px;
          }
        }
      </style>

      <a class="back-link" href="${this.escapeAttribute(this.backHref)}">Volver al listado</a>
      <section class="hero hero-detail">
        ${media}
        <div class="hero-copy">
          <p class="section-kicker">Detalle editorial</p>
          <h1>${this.escapeHtml(this.hero.title)}</h1>
          ${this.hero.subtitle ? `<p class="subtitle">${this.escapeHtml(this.hero.subtitle)}</p>` : ''}
            <div class="hero-pills">
              ${this.hero.pills
                .map(
                  (pill) =>
                    `<a class="hero-pill-link" href="${this.escapeAttribute(
                      buildAnimaliaTaxonomyCanonicalRoute(pill.filterKey, pill.value),
                    )}"><span class="hero-pill">${this.escapeHtml(pill.label)}: ${this.escapeHtml(pill.value)}</span></a>`,
                )
                .join('')}
            </div>
        </div>
      </section>
    `;
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  private escapeAttribute(value: string): string {
    return this.escapeHtml(value);
  }

  private renderLicenseBadge(): string {
    if (!this.hero?.imageLicenseName) {
      return '';
    }

    const label = `© ${this.escapeHtml(this.hero.imageLicenseName)}`;

    if (this.hero.imageLicenseUrl) {
      return `<a class="media-badge" href="${this.escapeAttribute(this.hero.imageLicenseUrl)}" target="_blank" rel="noreferrer">${label}</a>`;
    }

    return `<span class="media-badge">${label}</span>`;
  }

  private renderArtistBadge(): string {
    if (!this.hero?.imageArtist) {
      return '';
    }

    return `<span class="media-badge">✎ ${this.escapeHtml(this.hero.imageArtist)}</span>`;
  }

  private renderAttributionBadge(): string {
    if (!this.hero?.imageAttributionRequired) {
      return '';
    }

    const title = this.hero.imageCredit ?? this.hero.imageSource ?? 'Atribucion requerida';

    return `<span class="media-badge" title="${this.escapeAttribute(title)}">✓ atribucion</span>`;
  }
}

if (!customElements.get('mv-animalia-detail-hero')) {
  customElements.define('mv-animalia-detail-hero', MvAnimaliaDetailHero);
}
